/**
 * The understanding pipeline seam.
 *
 * V0-prototype: resolves the synthetic fixture after a simulated latency.
 *
 * NEXT (agreed 2026-06-04, deferred): this is where the live vision-LLM call
 * plugs in. The contract stays the same - pages/file in, CapturedDocument
 * out - so Processing.tsx and everything downstream won't change:
 *
 *   understand({ pageImages, fixture }) ->
 *     POST page images to the model with the bounded-guidance prompt ->
 *     { classification, summary, meaning, suggestedActions }
 *
 * Synthetic documents only until the DPIA clears real user docs (Mini PRD
 * open question #4).
 */
import { type CapturedDocument } from './fixtures';

const SIMULATED_LATENCY_MS = 2600;

export class CaptureFailedError extends Error {
  constructor(public reason: string) {
    super(reason);
    this.name = 'CaptureFailedError';
  }
}

export interface UnderstandInput {
  /** The fixture the mock pickers resolved. The live pipeline will instead
   * take raw page images / file bytes. */
  fixture: CapturedDocument;
}

export const understand = async ({
  fixture,
}: UnderstandInput): Promise<CapturedDocument> => {
  await new Promise((resolve) => setTimeout(resolve, SIMULATED_LATENCY_MS));
  if (fixture.willFail) {
    throw new CaptureFailedError(
      fixture.failReason ?? "We couldn't read this document.",
    );
  }
  return fixture;
};
